import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import TimelineYearMarker from '../components/chapters/timeline/TimelineYearMarker';
import TimelineMilestoneCard from '../components/chapters/timeline/TimelineMilestoneCard';
import TimelineMemoryCard from '../components/chapters/timeline/TimelineMemoryCard';
import TimelineQuestionCard from '../components/chapters/timeline/TimelineQuestionCard';
import { useChapterTimeline } from '../hooks/useChaptersData';

/**
 * ChapterExpandedView Page
 *
 * Full chapter view with timeline of milestones, memories, and questions.
 * Matches the chapter-expanded-view SVG mockup.
 */
function ChapterExpandedView() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const {
    chapter,
    timeline,
    waitingQuestions,
    loading,
    error,
  } = useChapterTimeline(chapterId);

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive mx-auto mb-4"></div>
                <p className="font-serif text-gray-600 text-lg">Loading chapter...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error || !chapter) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
              <span className="text-5xl mb-4 block">😔</span>
              <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">
                Chapter not found
              </h3>
              <p className="font-serif text-gray-500 text-base mb-4">
                We couldn't load this chapter.
              </p>
              <Link
                to="/my-life"
                className="text-fb-olive hover:underline font-semibold"
              >
                ← Back to My Life
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const endYearDisplay = chapter.end_year || 'Present';

  // Handle navigation
  const handleViewMilestone = (milestone) => {
    // Milestones don't have life_event link, use milestone detail view
    navigate(`/my-life/milestones/${milestone.id}`);
  };

  const handleViewMemory = (memory) => {
    // Navigate to story view if linked, otherwise to memory detail view
    if (memory.life_event_id) {
      navigate(`/stories/${memory.life_event_id}`);
    } else {
      navigate(`/my-life/memories/${memory.id}`);
    }
  };

  const handleAnswerQuestion = (question) => {
    // Navigate to add memory with the question context
    navigate(`/my-life/chapters/${chapterId}/add-memory`, {
      state: { questionId: question.id, questionText: question.question }
    });
  };

  const handleAddToChapter = () => {
    navigate('/tell-a-story');
  };

  // Get sorted years
  const years = Object.keys(timeline).sort((a, b) => {
    if (a === 'undated') return 1;
    if (b === 'undated') return -1;
    return parseInt(a) - parseInt(b);
  });

  return (
    <Layout>
      <div className="min-h-screen bg-fb-cream">
        {/* Breadcrumb */}
        <div className="bg-fb-cream border-b border-gray-200 px-4 py-3">
          <div className="max-w-6xl mx-auto">
            <Link
              to="/my-life"
              className="text-fb-olive hover:underline font-sans text-sm"
            >
              ← Back to My Life
            </Link>
          </div>
        </div>

        {/* Chapter Banner */}
        <div className="bg-fb-olive text-white">
          <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="font-sans text-xs text-white/70 uppercase tracking-wide mb-1">
                  Chapter {chapter.number}
                </p>
                <h1 className="font-serif text-2xl font-bold mb-1">
                  "{chapter.title}"
                </h1>
                <p className="font-sans text-sm text-white/85">
                  {chapter.start_year} – {endYearDisplay}
                </p>
              </div>

              <div className="flex flex-col md:items-end gap-2">
                <p className="font-sans text-xs text-white/85">
                  {chapter.milestone_count || 0} milestones · {chapter.memory_count || 0} memories
                </p>
                <p className="font-sans text-xs text-white/85">
                  {chapter.photo_count || 0} photos · 🎙 {Math.round(chapter.voice_minutes || 0)} min of stories
                </p>
                <button
                  onClick={() => navigate(`/my-life/chapters/${chapterId}/edit`)}
                  className="
                    bg-fb-terra hover:bg-fb-terra/90 text-white
                    font-semibold text-sm py-2 px-4 rounded-lg
                    transition-colors duration-200
                    flex items-center gap-1
                  "
                >
                  <span>✏️</span>
                  <span>Edit Chapter</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Timeline (70%) */}
            <div className="lg:w-[70%]">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                {/* Vertical timeline line */}
                <div className="relative">
                  <div
                    className="
                      absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200
                    "
                    style={{ transform: 'translateX(-50%)' }}
                  />

                  {/* Timeline content */}
                  <div className="space-y-4">
                    {years.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p className="font-serif text-lg mb-2">
                          No stories in this chapter yet
                        </p>
                        <p className="font-sans text-sm">
                          {chapter.starter_prompt || 'Start by adding a milestone or memory.'}
                        </p>
                      </div>
                    ) : (
                      years.map((year) => (
                        <div key={year}>
                          {/* Year marker */}
                          {year !== 'undated' && (
                            <TimelineYearMarker year={year} />
                          )}

                          {/* Items for this year */}
                          <div className="space-y-3 mt-2">
                            {timeline[year].map((item) => {
                              if (item.type === 'milestone') {
                                return (
                                  <TimelineMilestoneCard
                                    key={`m-${item.data.id}`}
                                    milestone={item.data}
                                    onView={handleViewMilestone}
                                  />
                                );
                              }
                              if (item.type === 'memory') {
                                return (
                                  <TimelineMemoryCard
                                    key={`mem-${item.data.id}`}
                                    memory={item.data}
                                    onView={handleViewMemory}
                                  />
                                );
                              }
                              if (item.type === 'question') {
                                return (
                                  <TimelineQuestionCard
                                    key={`q-${item.data.id}`}
                                    question={item.data}
                                    onAnswer={handleAnswerQuestion}
                                  />
                                );
                              }
                              return null;
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add to chapter button */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleAddToChapter}
                      className="
                        w-full py-4 px-6
                        border-2 border-dashed border-fb-olive rounded-xl
                        text-fb-olive font-semibold
                        hover:bg-fb-olive/5
                        transition-colors duration-200
                        text-center
                      "
                    >
                      + Add to this chapter
                      <br />
                      <span className="font-normal text-sm">
                        Add a milestone, memory, photo, or recording
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar (30%) */}
            <div className="lg:w-[30%] space-y-4">
              {/* Chapter Navigation */}
              <ChapterNavSidebar
                currentChapterId={chapterId}
                onSelectChapter={(id) => navigate(`/my-life/chapters/${id}`)}
              />

              {/* Chapter Stats */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <h3 className="font-serif font-bold text-base text-gray-800 mb-4">
                  This Chapter
                </h3>
                <div className="space-y-3">
                  <StatRow value={chapter.milestone_count || 0} label="milestones" />
                  <StatRow value={chapter.memory_count || 0} label="memories" />
                  <StatRow value={chapter.photo_count || 0} label="photos" />
                  <StatRow
                    value={`${Math.round(chapter.voice_minutes || 0)} min`}
                    label="of voice stories"
                  />
                </div>
              </div>

              {/* Unanswered Questions */}
              {waitingQuestions.length > 0 && (
                <div className="bg-fb-warm rounded-xl border-2 border-fb-terra shadow-sm p-5">
                  <h3 className="font-serif font-bold text-sm text-fb-terra mb-3">
                    {waitingQuestions.length} Question{waitingQuestions.length !== 1 ? 's' : ''} Waiting
                  </h3>
                  {waitingQuestions.slice(0, 1).map((q) => (
                    <div key={q.id} className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 rounded-full bg-fb-olive flex items-center justify-center">
                          <span className="text-white text-xs font-bold">
                            {(q.asker_name || 'A')[0]}
                          </span>
                        </div>
                        <span className="font-sans text-xs text-gray-700">
                          {q.asker_name || 'A family member'} asked:
                        </span>
                      </div>
                      <p className="font-sans text-sm text-gray-800 italic mb-3">
                        "{q.question}"
                      </p>
                    </div>
                  ))}
                  <button
                    onClick={() => handleAnswerQuestion(waitingQuestions[0])}
                    className="
                      w-full bg-fb-terra hover:bg-fb-terra/90 text-white
                      font-semibold text-sm py-2 px-4 rounded-lg
                      transition-colors duration-200
                      flex items-center justify-center gap-1
                    "
                  >
                    <span>🎙</span>
                    <span>Answer {waitingQuestions[0]?.asker_name || 'Question'}</span>
                  </button>
                  {waitingQuestions.length > 1 && (
                    <p className="text-center text-xs text-fb-terra mt-2">
                      + {waitingQuestions.length - 1} more question{waitingQuestions.length > 2 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Add to Story - Same style as MyLifeChapters */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <h3 className="font-serif font-bold text-lg text-gray-800 mb-4">
                  Add to Your Story
                </h3>
                <div className="space-y-3">
                  {/* Add Milestone */}
                  <button
                    onClick={() => navigate(`/my-life/chapters/${chapterId}/add-milestone`)}
                    className="
                      w-full bg-fb-olive hover:bg-fb-olive/90 text-white
                      font-semibold py-3 px-4 rounded-xl
                      transition-colors duration-200
                      flex items-center justify-center gap-2
                    "
                  >
                    <span>🏆</span>
                    <span>Add Milestone</span>
                  </button>

                  {/* Add Memory */}
                  <button
                    onClick={() => navigate('/tell-a-story')}
                    className="
                      w-full bg-fb-terra hover:bg-fb-terra/90 text-white
                      font-bold py-3 px-4 rounded-xl
                      transition-colors duration-200
                      flex items-center justify-center gap-2
                    "
                  >
                    <span>❤️</span>
                    <span>Add Memory</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/**
 * Stat row for sidebar
 */
function StatRow({ value, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-sans text-xl font-bold text-fb-olive">
        {value}
      </span>
      <span className="font-sans text-sm text-gray-600">
        {label}
      </span>
    </div>
  );
}

/**
 * Chapter Navigation Sidebar (uses useChaptersData)
 */
function ChapterNavSidebar({ currentChapterId, onSelectChapter }) {
  const { chapters } = require('../hooks/useChaptersData').useChaptersData();

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <h3 className="font-serif font-bold text-base text-gray-800 mb-3">
        📖 All Chapters
      </h3>
      <div className="space-y-2">
        {chapters.map((ch) => {
          const isActive = ch.id === currentChapterId;
          return (
            <button
              key={ch.id}
              onClick={() => onSelectChapter(ch.id)}
              className={`
                w-full text-left py-2 px-3 rounded-lg
                font-sans text-sm
                transition-colors duration-200
                ${isActive
                  ? 'bg-fb-olive text-white font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              Ch {ch.number}: "{ch.title}"
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ChapterExpandedView;
